"use client";

import React from "react";
import { CircleCheck as CheckCircle2, Clock, PhoneCall, ShieldCheck, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const keyBenefits = [{
  id: 1,
  title: "Fast 24/7 Response",
  description: "On-site emergency arrival in Jonesboro & Clayton County."
}, {
  id: 2,
  title: "Upfront Transparent Pricing",
  description: "No hidden charges or surprise weekend fees."
}, {
  id: 3,
  title: "Licensed & Insured Techs",
  description: "Equipped to fix clogs, severe leaks, and full replacements."
}];
export default function ToiletHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="toiletrepairhero" data-nav="light" className="relative overflow-hidden bg-background py-12 md:py-20 lg:py-24 border-b border-border">
      {/* Background Decorative Blur */}
      <div className="pointer-events-none absolute -top-24 left-1/2 -z-10 h-96 w-full -translate-x-1/2 rounded-full bg-secondary/60 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Content Column */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="lg:col-span-7 flex flex-col items-start space-y-6">
            <motion.div variants={itemVariants}>
              <Badge variant="secondary" className="gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                <Clock className="size-3.5 text-accent" />
                24/7 Emergency Toilet Repair
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl">
              Reliable Toilet Repair & Replacement in Jonesboro, GA
            </motion.h1>

            <motion.p variants={itemVariants} className="max-w-2xl text-base text-muted-foreground sm:text-lg leading-relaxed">
              From overflowing toilets and stubborn clogs to running tanks and leaking seals, our licensed Jonesboro plumbers arrive rapidly to restore comfort and prevent costly water damage.
            </motion.p>

            {/* CTA Group */}
            <motion.div variants={itemVariants} className="flex w-full flex-col gap-3.5 sm:w-auto sm:flex-row sm:items-center pt-2">
              <Button asChild size="lg" className="h-12 px-6 font-semibold shadow-md gap-2.5">
                <a href="tel:4708479076">
                  <PhoneCall className="size-5 text-accent" />
                  Call Now: (470) 847-9076
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 px-6 font-medium">
                <Link to="/contact/">
                  Request Service Online
                </Link>
              </Button>
            </motion.div>

            {/* Quick Guarantees / Highlights */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 gap-3 pt-6 sm:grid-cols-3 w-full border-t border-border/80">
              {keyBenefits.map((benefit, i) => <div key={benefit.id} data-index={i} className="flex flex-col space-y-1 rounded-lg bg-card/60 p-3 border border-border/60">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                    <span>{benefit.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {benefit.description}
                  </p>
                </div>)}
            </motion.div>
          </motion.div>

          {/* Visual Column */}
          <motion.div initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          scale: shouldReduceMotion ? 1 : 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="lg:col-span-5">
            <div className="relative mx-auto w-full max-w-md lg:max-w-none">
              <Card className="overflow-hidden border-border bg-card shadow-xl">
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/toilet_repair_service_expert.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/toilet_repair_service_expert.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/toilet_repair_service_expert.png-wvc-sizes" alt="Plumber inspecting and repairing a residential toilet fixture" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-primary text-primary-foreground font-semibold shadow-xs">
                      Jonesboro Local
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-5 sm:p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                      <Wrench className="size-5" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-foreground leading-snug">
                        Immediate Diagnostic & Repair
                      </h2>
                      <p className="text-xs text-muted-foreground">
                        Same-day dispatch across Clayton County
                      </p>
                    </div>
                  </div>

                  <div className="rounded-md bg-muted/60 p-3.5 text-xs text-muted-foreground border border-border/50 flex items-start gap-2.5">
                    <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>
                      Every repair is backed by our full 100% satisfaction guarantee and clean-home promise.
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-1 text-xs">
                    <span className="font-medium text-foreground">Response Window:</span>
                    <span className="font-semibold text-chart-4">Typically under 45 mins</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}